"use client";

import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@bidbattles/bloks/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandLoading,
} from "@bidbattles/bloks/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@bidbattles/bloks/components/ui/popover";
import { cn } from "@bidbattles/bloks/lib/ui";
import { type BaseInputVariantProps, baseInput, inputWrapper } from "./input";

export type ComboboxItem<Value extends string> = {
	value: Value;
	label: React.ReactNode;
	searchValue?: string;
};

type CommonProps<Value extends string> = {
	id?: string;
	items: ComboboxItem<Value>[];
	onBlur?: () => void;
	placeholder: string;
	className?: string;
	search?: string;
	onSearchChange?: (search: string) => void;
	disabled?: boolean;
	disableSearch?: boolean;
	loading?: boolean;
	Empty?: React.ReactElement;
};
export type ComboboxProps<Value extends string> = BaseInputVariantProps &
	CommonProps<Value> &
	(
		| {
				multiple: false;
				value: Value | null;
				onChange: (value: Value | null) => void;
		  }
		| {
				multiple: true;
				values: Set<Value>;
				onChange: (values: Set<Value>) => void;
		  }
	);

export function Combobox<Value extends string>({
	id,
	items,
	onBlur,
	placeholder,
	className,
	search: searchProp,
	onSearchChange,
	disabled,
	disableSearch,
	loading,
	Empty,
	variant = "box",
	...props
}: ComboboxProps<Value>) {
	const [open, setOpen] = React.useState(false);
	const [_search, _setSearch] = React.useState("");
	const [search, setSearch] = [
		searchProp ?? _search,
		onSearchChange ?? _setSearch,
	];

	const isSelected = (value: Value) =>
		props.multiple ? props.values.has(value) : props.value === value;
	const hasValue = props.multiple ? props.values.size > 0 : !!props.value;
	const label = props.multiple
		? [...props.values.values()]
				.map((value) => items.find((item) => item.value === value)?.label)
				.join(", ")
		: items.find((item) => item.value === props.value)?.label;

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			onOpenChangeComplete={(open) => !open && onBlur?.()}
		>
			<PopoverTrigger
				disabled={disabled}
				render={
					<Button
						id={id}
						variant="unstyled"
						role="combobox"
						aria-expanded={open}
						className={cn(
							baseInput({ variant }),
							inputWrapper({ variant }),
							"w-full justify-between outline-hidden px-2",
							hasValue ? "text-gray-900" : "text-gray-500",
							className,
						)}
					/>
				}
			>
				<span className="w-full overflow-ellipsis overflow-hidden flex justify-start">
					{hasValue ? label : placeholder}
				</span>

				<ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50 text-gray-500" />
			</PopoverTrigger>
			<PopoverContent align="start" className="max-w-[95%] min-w-[10rem] p-0">
				<Command
					filter={(value, search) => {
						const item = items.find((item) => item.value === value);
						if (!item) return 0;

						const textRepr = item.searchValue ?? item.label?.toString() ?? "";
						return textRepr.toLowerCase().includes(search.toLowerCase())
							? 1
							: 0;
					}}
				>
					{!disableSearch && (
						<CommandInput
							value={search}
							onValueChange={setSearch}
							placeholder="Search..."
						/>
					)}
					<CommandList>
						{!loading &&
							(Empty ? Empty : <CommandEmpty>No results.</CommandEmpty>)}
						{loading && items.length === 0 && <CommandLoading />}
						<CommandGroup>
							{items.map((item) => (
								<CommandItem
									key={item.value}
									value={item.value}
									onSelect={(currentValue) => {
										const cValue = currentValue as Value;
										if (props.multiple) {
											const newValues = new Set(props.values);
											if (newValues.has(cValue)) {
												newValues.delete(cValue);
											} else {
												newValues.add(cValue);
											}

											props.onChange(newValues);
										} else {
											props.onChange(
												currentValue === props.value
													? null
													: (currentValue as Value),
											);
										}

										setOpen(false);
										setSearch("");
									}}
								>
									<CheckIcon
										className={cn(
											"mr-2 h-4 w-4",
											isSelected(item.value) ? "opacity-100" : "opacity-0",
										)}
									/>
									{item.label}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
